import { useEffect, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAssignees, usePerson } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { JournalDetail } from '@/components/console/JournalPanel';
import { StoppedWhy } from '@/components/console/StoppedWhy';
import { TheyWrote } from '@/components/console/TheyWrote';
import { ToldUs } from '@/components/console/ToldUs';
import { UnderstandingSection } from '@/components/console/Understanding';
import { FullBodySection } from '@/components/console/FullBodySection';
import { AwarenessChecksSection } from '@/components/console/AwarenessChecksSection';
import { EMPTY_DRAFT, OutreachComposer, type ReplyTarget } from '@/components/console/OutreachComposer';
import { KIND_ICON, KIND_LABEL, dayLabel, isJournalKind, timeOf } from '@/lib/person-journal';
import { DOOR_LABEL, NEED_LABEL, PLAN_LABEL, PLATFORM_LABEL, REENGAGE_DELIVERY_SUMMARY, SOURCE_LABEL, STAGE_LABEL, CANCEL_NOT_CONFIRMED, cancelPersonSubscription, knowledgeRows, reengagePerson, variableMovementLine, momentLine, type InboundMessageView, type Plan, type ReengageResult, type TimelineEvent } from '@/lib/people-data';
import { NOT_MEASURED, ago, day, num, plural, when } from '@/lib/format';
import s from './console.module.css';

/**
 * One person (Brian, 2026-09-18): the facts, the "where they stopped" line,
 * what they wrote us and what they told us in their own words, what Elina
 * knows, their asks, the whole journey newest first, the admin notes, and
 * two buttons that say what they do: "Send a message" reaches the person
 * (pv-auth #332, the one composer, which also answers one of their inbound
 * messages in place, #336); "Create a task for us (sends nothing)" makes a
 * GHL task for Brian or Linda and writes a journal entry, and nothing goes
 * to the person. The response's own `deliverySummary` is printed under each.
 *
 * Nothing on this page is inferred. A field the server could not measure
 * says so; the tag beside a text is a keyword suggestion, never a claim.
 */

const EXTRA_ICON: Record<string, string> = { signup: '★', verified: '✓', login: '→', personal_note: '✉', feedback: '!', day_two_promise: '◔', they_said: '“', elina_said: '”', cancellation: '×', one_time_look: '◎', trial: '◷', subscription: '$', first_check_attempt: '↻', inbound_message: '✉', pain_stop: '⚑' };
const EXTRA_LABEL: Record<string, string> = { signup: 'Signed up', verified: 'Verified', login: 'Signed in', personal_note: 'Personal note', feedback: 'Something wrong', day_two_promise: 'Day-two promise', they_said: 'They said', elina_said: 'Elina said', cancellation: 'Cancelled', one_time_look: 'One-time look', trial: 'Trial week', subscription: 'Subscription', first_check_attempt: 'First-check attempt', inbound_message: 'They wrote', pain_stop: 'Said it hurt' };

const toReply = (m: InboundMessageView): ReplyTarget => ({ messageId: m.id, channel: m.channel, subject: m.subject, receivedAt: m.receivedAt });

function EventRow({ e }: { e: TimelineEvent }) {
  const [open, setOpen] = useState(false);
  const rows = Object.entries(e.data ?? {}).length;
  return (
    <div className={s.jEntry}>
      <span className={s.jIcon} aria-hidden="true">{isJournalKind(e.kind) ? KIND_ICON[e.kind] : EXTRA_ICON[e.kind] ?? '·'}</span>
      <div className={s.jBody}>
        <div className={s.jLine}>
          <span className={s.jTime}>{timeOf(e.at)}</span>
          <span className={s.jKind}>{isJournalKind(e.kind) ? KIND_LABEL[e.kind] : EXTRA_LABEL[e.kind] ?? e.kind.replace(/_/g, ' ')}</span>
          <span className={s.jSummary}>{e.summary}</span>
          {e.kind === 'session_trace' && typeof e.data?.href === 'string' && <Link className={s.linkA} to={e.data.href as string} data-action="open-session">Open the session page</Link>}
        </div>
        {rows > 0 && (
          <button type="button" className={s.linkBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide detail' : `Show detail (${rows})`}</button>
        )}
        {open && <JournalDetail data={e.data} />}
      </div>
    </div>
  );
}

/** A GHL task for a human. It never messages the person, and the button and the result both say so. */
function CreateTask({ userId, onDone }: { userId: string; onDone: (r: ReengageResult) => void }) {
  const assignees = useAssignees();
  // ?reengage=1 opens the panel on arrival: the phone card's Re-engage button links here.
  const [params] = useSearchParams();
  const [open, setOpen] = useState(params.get('reengage') === '1');
  const [assignee, setAssignee] = useState<string>('');
  const [note, setNote] = useState('');
  const [due, setDue] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const list = assignees.data?.assignees ?? [];
  const chosen = list.find((a) => a.id === assignee) ?? null;

  const send = async () => {
    if (!note.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await reengagePerson({ userId, assigneeId: chosen?.id ?? null, assigneeName: chosen?.name ?? null, note: note.trim(), dueInDays: due });
      const head = r.ok ? `GHL task created${chosen ? ` for ${chosen.name}` : ''}${r.ghlTaskId ? ` (${r.ghlTaskId})` : ''}. Journal entry written.` : `Journal entry written. GHL task not created: ${r.ghlError ?? 'no reason given'}`;
      setResult(`${head} ${r.deliverySummary ?? REENGAGE_DELIVERY_SUMMARY}`);
      setNote('');
      onDone(r);
    } catch (err) {
      setResult(`Not created: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return <button type="button" className={s.btnGhost} onClick={() => setOpen(true)} data-action="create-task">Create a task for us (sends nothing)</button>;
  return (
    <div className={s.reengage} data-panel="reengage">
      <div className={s.draftHead}>
        <strong>Create a task for us: a GHL task. Nothing goes to the person.</strong>
        <button type="button" className={s.linkBtn} onClick={() => setOpen(false)}>Close</button>
      </div>
      <label className={s.sub}>Assign to
        <select className={s.selectInline} style={{ marginLeft: 8 }} value={assignee} onChange={(e) => setAssignee(e.target.value)} aria-label="Assignee">
          <option value="">{list.length ? 'Unassigned' : 'Nobody available'}</option>
          {list.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </label>
      {assignees.data && list.length === 0 && <div className={s.jWarn}>{assignees.data.reason ?? 'No assignees configured.'} The task is created unassigned.</div>}
      <textarea className={s.draftBody} rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What to do and why, in your words" aria-label="Task note" />
      <div className={s.draftActions}>
        <label className={s.sub}>Due in
          <select className={s.selectInline} style={{ marginLeft: 8 }} value={due} onChange={(e) => setDue(Number(e.target.value))} aria-label="Due in days">
            {[0, 1, 2, 3, 7].map((n) => <option key={n} value={n}>{n === 0 ? 'today' : n === 1 ? '1 day' : `${n} days`}</option>)}
          </select>
        </label>
        <button type="button" className={s.btn} disabled={busy || !note.trim()} onClick={() => void send()}>{busy ? 'Creating' : 'Create the task (sends nothing)'}</button>
        {result ? <span className={s.sub} data-result>{result}</span> : <span className={s.sub}>{REENGAGE_DELIVERY_SUMMARY}</span>}
      </div>
    </div>
  );
}

/** Plans someone pays for (or is on a paid trial of). Free and comp have nothing to cancel. */
const CANCELLABLE_PLANS: ReadonlySet<Plan> = new Set<Plan>(['trial', 'paid_monthly', 'paid_annual', 'paid_other']);

/**
 * The admin cancel (pv-auth POST /admin/subscriptions/cancel): at the end of
 * the period they have paid for, never today. Two clicks, and the line under
 * it is the server's answer: the end date from Stripe on success, the
 * server's own message (Stripe's words, or the store that bills them) on a
 * failure. A failure is never shown as done.
 */
function CancelSubscription({ userId, onDone }: { userId: string; onDone: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await cancelPersonSubscription(userId);
      const date = r.endsAt.slice(0, 10);
      setResult({ ok: true, text: r.alreadyCancelled ? `Already set to cancel. It ends ${date}.` : `Set to cancel at the end of the paid period. It ends ${date}.` });
      setConfirming(false);
      onDone();
    } catch (err) {
      // apiPost throws with the server's own message and attaches the response.
      const e = err as Error & { response?: { status?: number } };
      const msg = e.message || 'no message';
      let text: string;
      if (e.response) text = msg.startsWith('Not cancelled') ? msg : `Not cancelled (${e.response.status ?? 'error'}): ${msg}`;
      else if (msg === CANCEL_NOT_CONFIRMED) text = msg;
      else text = `Not cancelled: the server could not be reached (${msg}).`;
      setResult({ ok: false, text });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-panel="cancel-subscription">
      {!confirming
        ? <button type="button" className={s.btnGhost} onClick={() => { setConfirming(true); setResult(null); }} data-action="cancel-subscription">Cancel at period end</button>
        : (
          <span className={s.sub}>
            It ends when the period they paid for ends. Nothing is refunded.{' '}
            <button type="button" className={s.btn} disabled={busy} onClick={() => void run()} data-action="confirm-cancel">{busy ? 'Cancelling' : 'Confirm: cancel at period end'}</button>{' '}
            <button type="button" className={s.linkBtn} disabled={busy} onClick={() => setConfirming(false)}>Keep it</button>
          </span>
        )}
      {result && (result.ok
        ? <div className={s.sub} role="status" data-result="cancelled">{result.text}</div>
        : <div className={s.error} role="alert" data-result="cancel-failed">{result.text}</div>)}
    </div>
  );
}

export default function PersonPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const qc = useQueryClient();
  const q = usePerson(id ?? null);
  const d = q.data;
  const p = d?.person;
  const [kindFilter, setKindFilter] = useState<string>('all');
  const kinds = d ? Array.from(new Set(d.timeline.map((e) => e.kind))).sort() : [];
  const shown = d ? d.timeline.filter((e) => kindFilter === 'all' || e.kind === kindFilter) : [];
  const byDay = new Map<string, TimelineEvent[]>();
  for (const e of shown) { const k = e.at.slice(0, 10); byDay.set(k, [...(byDay.get(k) ?? []), e]); }

  // The one composer: "Send a message" opens it empty; Reply on a message (here or from Needs via ?reply=) opens it set to answer that message.
  const [composerOpen, setComposerOpen] = useState(params.get('message') === '1' || !!params.get('reply'));
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  useEffect(() => {
    const wanted = params.get('reply');
    if (!wanted || !d?.inbound) return;
    const m = d.inbound.find((x) => x.id === wanted);
    if (m) { setReplyTo(toReply(m)); setComposerOpen(true); }
  }, [d, params]);
  const openReply = (m: InboundMessageView) => {
    setReplyTo(toReply(m));
    setComposerOpen(true);
    document.getElementById('message-panel')?.scrollIntoView({ block: 'center' });
  };
  const openNew = () => { setReplyTo(null); setComposerOpen(true); };
  const closeComposer = () => { setComposerOpen(false); setReplyTo(null); };
  const refreshPerson = () => { if (p) void qc.invalidateQueries({ queryKey: ['person', p.userId] }); void qc.invalidateQueries({ queryKey: ['people-needs'] }); void qc.invalidateQueries({ queryKey: ['people'] }); };

  const inbound = d?.inbound;
  const unreadCount = inbound ? inbound.filter((m) => !m.readAt).length : 0;
  const painCount = d?.painEvents?.length ?? 0;
  const toldCount = d ? (d.productFeedback?.length ?? 0) + (d.eveningNotes?.length ?? 0) + painCount : 0;

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <div>
          <div className={s.sub}><Link to="/people">People</Link> · person</div>
          <h1 className={s.pageTitle}>{p ? (p.name || p.email) : q.isError ? 'Not found' : 'Loading'}</h1>
          {p && <div className={s.sub}>{p.email}{p.phone ? ` · ${p.phone}` : ''} · joined {day(p.signupAt)} · timezone {p.timezone ?? 'not recorded'}{p.qa ? ' · QA account' : ''}</div>}
        </div>
        {p && (
          <div className={s.panelActions}>
            {p.ghlUrl ? <a className={s.btnGhost} href={p.ghlUrl} target="_blank" rel="noreferrer">Open in GHL</a> : <span className={s.missing}>no GHL contact yet</span>}
            <Link className={s.btnGhost} to={`/people?user=${p.userId}&journal=1&section=write`}>Journal and notes</Link>
            <Link className={s.btnGhost} to={`/people/${encodeURIComponent(p.userId)}/sessions`} data-action="sessions">Sessions</Link>
            <button type="button" className={s.btn} onClick={openNew} data-action="send-message" aria-expanded={composerOpen && !replyTo}>Send a message</button>
            <CreateTask userId={p.userId} onDone={refreshPerson} />
            {CANCELLABLE_PLANS.has(p.plan) && <CancelSubscription userId={p.userId} onDone={refreshPerson} />}
          </div>
        )}
      </div>

      {q.isError && <div className={s.error}>Could not load this person. This page needs GET /provider/admin/people/:id.</div>}

      {d && p && (
        <>
          <div className={s.stopLine} data-line="stopped-at">{d.stoppedAt.line}</div>

          {momentLine(d.moment) && <div className={s.sub} data-line="moment">{momentLine(d.moment)}</div>}

          {composerOpen && (
            <div id="message-panel" className={s.messagePanel}>
              <OutreachComposer
                key={replyTo?.messageId ?? 'new'}
                via="people"
                p={{ userId: p.userId, phone: p.phone, draft: EMPTY_DRAFT }}
                replyTo={replyTo}
                onSent={refreshPerson}
                onClose={closeComposer}
              />
            </div>
          )}

          <StoppedWhy person={p} attempts={d.attempts ?? []} />

          {p.variableReadings && p.variableReadings.movements.length > 0 && (
            <div className={s.notice} data-panel="variable-readings">
              <strong>Variable readings</strong> since {day(p.variableReadings.flaggedAt)}
              {p.variableReadings.lastAlertAt ? ` · posted to Slack ${ago(p.variableReadings.lastAlertAt)}` : ' · not posted to Slack'}
              <ul>
                {p.variableReadings.movements.map((m) => (
                  <li key={m.movementId} data-movement={m.movementId}>{variableMovementLine(m)}</li>
                ))}
              </ul>
            </div>
          )}

          <UnderstandingSection key={p.userId} userId={p.userId} defaultOpen={location.hash === '#understanding'} />

          <div className={s.strip}>
            <div className={s.stat}><span className={s.statLabel}>Stage</span><span className={s.statVal} style={{ fontSize: '1.1rem' }}>{STAGE_LABEL[p.stage]}</span><span className={s.statSub}>{PLAN_LABEL[p.plan]}{p.planDetail.planName ? ` · ${p.planDetail.planName}` : ''}{p.planDetail.interval ? ` · ${p.planDetail.interval}` : ''}{p.planDetail.trialEndsAt ? ` · ends ${day(p.planDetail.trialEndsAt)}` : ''}</span></div>
            <div className={s.stat}><span className={s.statLabel}>Last seen</span><span className={s.statVal} style={{ fontSize: '1.1rem' }}>{p.lastSeenAt ? ago(p.lastSeenAt) : 'never'}</span><span className={s.statSub}>{p.lastSeenSource ? `from ${p.lastSeenSource.replace(/_/g, ' ')}` : 'no row records a visit'}{p.daysSinceSeen !== null ? ` · ${plural(p.daysSinceSeen, 'day')} ago` : ''}</span></div>
            <div className={s.stat}><span className={s.statLabel}>Checks</span><span className={s.statVal}>{num(p.checks)}</span><span className={s.statSub}>{plural(p.checkDays, 'day')} · streak {p.streak === null ? 'no checks' : num(p.streak)} · last score {p.lastScore === null ? NOT_MEASURED : Math.round(p.lastScore)}</span></div>
            <div className={s.stat}><span className={s.statLabel}>Active Mode</span><span className={s.statVal}>{p.activeMinutesTotal === null ? <span style={{ fontSize: '0.9rem' }}>{NOT_MEASURED}</span> : `${num(p.activeMinutesTotal)} min`}</span><span className={s.statSub}>{p.activeMinutes7d === null ? 'none timed in 7 d' : `${num(p.activeMinutes7d)} min in 7 d`} · {num(p.activeSessions)} sessions{p.feelTags && p.feelTags.count > 0 ? ` · ${plural(p.feelTags.count, 'feel tag')}${p.feelTags.lastTag ? `, last "${p.feelTags.lastTag}"` : ''}` : ''}</span></div>
            <div className={s.stat}><span className={s.statLabel}>Platforms</span><span className={s.statVal} style={{ fontSize: '1.1rem' }}>{p.platforms.length ? p.platforms.map((x) => PLATFORM_LABEL[x]).join(', ') : 'none recorded'}</span><span className={s.statSub}>source: {p.source.label}{p.source.door ? ` · door ${DOOR_LABEL[p.source.door] ?? p.source.door}` : ''}</span></div>
          </div>

          <div className={s.personGrid}>
            <div className={s.stack}>
              <Section title="They wrote" summary={inbound ? `what they sent us through GHL, unread first${unreadCount ? ` · ${plural(unreadCount, 'unread message')}` : ''}` : 'what they sent us through GHL'} count={inbound ? inbound.length : null} defaultOpen={!inbound || unreadCount > 0 || (inbound.length > 0)} id="they-wrote">
                {inbound
                  ? <TheyWrote userId={p.userId} messages={inbound} onReply={openReply} onRead={refreshPerson} />
                  : <div className={s.notice}>Not on this API yet: their messages need pv-auth #336 deployed. Until then the thread is in GHL{p.ghlUrl ? <>: <a href={p.ghlUrl} target="_blank" rel="noreferrer">open the contact</a></> : ''}.</div>}
              </Section>

              <Section title="What they told us" summary={`${painCount ? `${plural(painCount, 'time')} they said it hurt and stopped, first · ` : ''}what they said about the product (the seventh-check question, what to improve) and their evening notes, unread first`} count={toldCount} defaultOpen={toldCount > 0} id="told-us">
                <ToldUs feedback={d.productFeedback ?? []} notes={d.eveningNotes ?? []} painEvents={d.painEvents} userId={p.userId} onRead={refreshPerson} />
              </Section>

              <Section title="Everything they typed" summary="every free-text field they typed, newest first; the tag is a keyword suggestion" count={d.toldUs.length}>
                {d.toldUs.length === 0 && <div className={s.empty}>Nothing typed yet.</div>}
                <div className={s.told}>
                  {d.toldUs.map((t, i) => (
                    <div key={`${t.at}-${i}`} className={`${s.toldRow} ${t.context?.flagged === true ? s.toldRowFlag : ''}`} data-flagged={t.context?.flagged === true ? '1' : undefined}>
                      <span className={s.jTime}>{when(t.at)}</span>
                      <span className={s.jKind}>{SOURCE_LABEL[t.source] ?? t.source}</span>
                      <span className={s.toldText}>{t.text}</span>
                      {t.context?.flagged === true ? <span className={`${s.pill} ${s.pill_flag}`}>flagged</span> : <span className={`${s.pill} ${s[`pill_${t.tag ?? 'untagged'}`]}`}>{t.tag ? `${NEED_LABEL[t.tag]}?` : 'untagged'}</span>}
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Journey" summary={`every event with its time, newest first${d.timelineCursor ? ' · the journal has older pages' : ''}`} count={d.timeline.length} defaultOpen>
                <div className={s.filters}>
                  <button type="button" className={`${s.filter} ${kindFilter === 'all' ? s.filterOn : ''}`} onClick={() => setKindFilter('all')}>All</button>
                  {kinds.map((k) => (
                    <button key={k} type="button" className={`${s.filter} ${kindFilter === k ? s.filterOn : ''}`} onClick={() => setKindFilter(k)}>{isJournalKind(k) ? KIND_LABEL[k] : EXTRA_LABEL[k] ?? k.replace(/_/g, ' ')}</button>
                  ))}
                </div>
                {d.unavailable.length > 0 && <div className={s.jWarn}>Not read this time: {d.unavailable.map((u) => u.source).join(', ')}.</div>}
                <div className={s.sub} style={{ marginTop: 8 }}>Emails sent are listed. Opens: {d.emailOpens === null ? 'not tracked' : num(d.emailOpens)}. {d.emailOpensReason}</div>
                {[...byDay.entries()].map(([k, list]) => (
                  <div key={k} className={s.jDay}>
                    <div className={s.jDayLabel}>{dayLabel(k)}</div>
                    {list.map((e) => <EventRow key={e.id} e={e} />)}
                  </div>
                ))}
                {shown.length === 0 && <div className={s.empty}>No events of that kind.</div>}
              </Section>
            </div>

            <div className={s.stack}>
              <Section title="What Elina knows" summary="latest answer per kind, from customize, the tour and chat" count={Object.keys(p.elina).length} defaultOpen>
                {Object.keys(p.elina).length === 0 && <div className={s.empty}>Nothing yet. Elina has not been told anything and has not asked.</div>}
                <dl className={s.facts}>
                  {knowledgeRows(p.elina).map((r) => (<div key={r.key} style={{ display: 'contents' }}><dt>{r.label}</dt><dd>{r.value}</dd></div>))}
                </dl>
              </Section>

              <Section title="Asks" summary="open tickets, feedback, exercise requests" count={p.openTickets + p.feedbackCount + p.exerciseRequests} defaultOpen={p.openTickets > 0}>
                <dl className={s.facts}>
                  <dt>Open tickets and help requests</dt><dd>{num(p.openTickets)}</dd>
                  <dt>Something wrong reports</dt><dd>{num(p.feedbackCount)}</dd>
                  <dt>Exercise requests</dt><dd>{num(p.exerciseRequests)}</dd>
                </dl>
                <div className={s.sub} style={{ marginTop: 8 }}>The texts are under "Everything they typed"; the ticket rows are in the journey.</div>
              </Section>

              <FullBodySection rows={d.fullBody} />

              <AwarenessChecksSection rows={d.awarenessChecks} />

              <Section title="Steps reached" summary="when each step happened" count={p.reached.length}>
                <dl className={s.facts}>
                  {p.reached.map((r) => (<div key={r.step} style={{ display: 'contents' }}><dt>{STAGE_LABEL[r.step]}</dt><dd>{r.at ? when(r.at) : <span className={s.missing}>time not recorded</span>}</dd></div>))}
                </dl>
              </Section>

              <Section title="Admin notes" summary="notes for Elina and for Brian, plus every task and message" count={d.notes.length} defaultOpen={d.notes.length > 0}>
                {d.notes.length === 0 && <div className={s.empty}>No notes yet.</div>}
                <div className={s.jNotes}>
                  {d.notes.map((n) => (
                    <div key={n.id} className={s.jNote}>{n.body}<div className={s.sub}>{n.authorEmail ?? 'unknown author'} · {when(n.createdAt)}</div></div>
                  ))}
                </div>
              </Section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
